import { useEffect, useMemo, useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { PBCC_PREMIUM } from '../config/siteMode.js'
import { AdminShell } from '../admin/AdminShell.jsx'
import { onValue, ref, update, remove, serverTimestamp } from 'firebase/database'

import { db, tref } from '../lib/firebase.js'
import CLUB from '../config/club.js'
import { TV_SCENES, normalizeTvMode, isCalcuttaScreen } from '../leaderboard/tvScenes.js'

// Optional emojis for the announcement message (TEST only).
const ANN_EMOJIS = ['⛳', '🏌️', '🏆', '🥇', '🎯', '📢', '⏰', '🍔', '🍻', '☀️', '🌧️', '🎉', '👏', '🔥', '⚡', '💪', '😊', '👍']

// ── /tvcontrol — per-TV remote control panel ──────────────────────────────
// ONE panel that controls every TV across ALL SIX real channels (Live Scores,
// Flight Brackets, Live Leaderboard, Course Scroll Map, Photo Wall, Split) plus
// Rotate — the same boards the tournament TV Monitor tab exposes. Auto-discovers
// every screen that has registered at tvControl/screens/{N} (the /tv?screen=N
// display heartbeats there) and renders one control row per screen — works for
// 2 TVs or 20, nothing hardcoded. Each row sets that screen's scene + rotate
// speed + which tournament it's pinned to (real or demo/practice). Panel WRITES,
// TVs LISTEN — the matching TV picks it up live (~1s), same real-time pattern
// as scores / GPS.

const SPEEDS = [15, 30, 60]
const LIVE_WINDOW_MS = 90000 // heartbeat is 30s → "live" if seen within 90s

const tName = (v) => String(v?.info?.name || '(unnamed)').split('\n')[0]
const tBadge = (v) => (v?.info?.isDemo ? (v?.info?.isPractice ? ' · Practice' : ' · Demo') : '')

// Inline, always-editable name for a screen row. Holds a local draft so typing
// is smooth; re-syncs from the stored value only while NOT focused (so a live
// RTDB update doesn't yank the cursor mid-edit). Writes on every change.
function ScreenNameInput({ value, onSave }) {
  const [draft, setDraft] = useState(value || '')
  const [focused, setFocused] = useState(false)
  useEffect(() => { if (!focused) setDraft(value || '') }, [value, focused])
  return (
    <input
      value={draft}
      onChange={(e) => { setDraft(e.target.value); onSave(e.target.value) }}
      onFocus={() => setFocused(true)}
      onBlur={() => setFocused(false)}
      placeholder="Name this TV (e.g. Bar)"
      title="Rename this TV — panel label only; does not change the screen number or /tv?screen=N URL"
      className="text-xl font-bold"
      style={{
        backgroundColor: 'transparent', color: PBCC_PREMIUM ? 'var(--ge-primary)' : '#FFFFFF', outline: 'none',
        border: 'none', borderBottom: `1px dashed ${PBCC_PREMIUM ? 'rgba(var(--ge-primary-rgb),0.35)' : 'rgba(255,255,255,0.30)'}`,
        padding: '1px 4px', minWidth: 130, maxWidth: 260,
      }}
    />
  )
}

export default function TvControlApp() {
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const [screens, setScreens] = useState({})
  const [tournaments, setTournaments] = useState({})
  const [now, setNow] = useState(Date.now())
  const [annText, setAnnText] = useState('')
  const [annTarget, setAnnTarget] = useState('all') // 'all' | screenId
  const [showEmoji, setShowEmoji] = useState(false)

  useEffect(() => {
    const off = onValue(tref('tvControl/screens'), (snap) => setScreens(snap.val() || {}))
    return () => off()
  }, [])

  useEffect(() => {
    const off = onValue(tref('tournaments'), (snap) => setTournaments(snap.val() || {}))
    return () => off()
  }, [])

  // Re-tick so the live/offline badges stay current without a refresh.
  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), 5000)
    return () => clearInterval(id)
  }, [])

  const setMode       = (id, mode) => update(tref(`tvControl/screens/${id}`), { mode }).catch((e) => console.warn('[tvcontrol] mode', e))
  const setSpeed      = (id, s)    => update(tref(`tvControl/screens/${id}`), { rotateSeconds: s }).catch((e) => console.warn('[tvcontrol] speed', e))
  const setTournament = (id, tid)  => update(tref(`tvControl/screens/${id}`), { tournamentId: tid || null }).catch((e) => console.warn('[tvcontrol] pin', e))
  // Panel-only label for the physical TV (e.g. "Bar", "Ball Room"). Stored on
  // tvControl/screens/{id}.name — the TV display never reads it, and the screen
  // NUMBER stays the identity (/tv?screen=N is unaffected). Blank clears it.
  const setName       = (id, name) => update(tref(`tvControl/screens/${id}`), { name: name.trim() || null }).catch((e) => console.warn('[tvcontrol] name', e))
  // Remove a registered screen entirely — deletes tvControl/screens/{id}. Use
  // when a TV is retired or was registered by mistake (there was no way to clear
  // stale screens 1/2/3/4 at the venue). If that /tv?screen=N page is still open
  // it will re-register on its next heartbeat, so close it first.
  const removeScreen  = (id, name) => {
    const label = name ? `TV ${id} — ${name}` : `TV ${id}`
    if (!window.confirm(`Remove ${label}?\n\nThis deletes the screen from the control panel. If that TV page is still open it will reappear on its next check-in — close /tv?screen=${id} first.`)) return
    remove(tref(`tvControl/screens/${id}`)).catch((e) => console.warn('[tvcontrol] remove', e))
  }

  // Tournament options for pinning — open events only, real first (by createdAt)
  // then demo/practice (by demoCreatedAt), each labeled. Demos ARE included so a
  // TV can be pinned to the current demo/practice event.
  const tournOptions = useMemo(() => {
    const open = Object.entries(tournaments).filter(([, v]) => v && typeof v === 'object' && v?.info?.status !== 'closed')
    const real = open.filter(([, v]) => !v?.info?.isDemo).sort((a, b) => (b[1]?.info?.createdAt || 0) - (a[1]?.info?.createdAt || 0))
    const demo = open.filter(([, v]) => !!v?.info?.isDemo).sort((a, b) => ((b[1]?.info?.demoCreatedAt || b[1]?.info?.createdAt || 0) - (a[1]?.info?.demoCreatedAt || a[1]?.info?.createdAt || 0)))
    return [...real, ...demo]
  }, [tournaments])

  // Calcutta "C" screens are a separate pool controlled from the Calcutta tab —
  // never show or target them here.
  const rows = Object.entries(screens).filter(([id]) => !isCalcuttaScreen(id)).sort((a, b) => {
    const na = Number(a[0]), nb = Number(b[0])
    if (Number.isFinite(na) && Number.isFinite(nb)) return na - nb
    return String(a[0]).localeCompare(String(b[0]))
  })

  // Announcements — write to the same tvControl channel the TVs already listen
  // on. Target 'all' → global tvControl/announcement (every screen overlays it);
  // a specific id → tvControl/screens/{id}/announcement (per-screen wins).
  const postAnnouncement = () => {
    const text = annText.trim()
    if (!text) return
    const ann = { text, postedAt: serverTimestamp() }
    const target = annTarget === 'all'
      ? update(tref('tvControl'), { announcement: ann })
      : update(tref(`tvControl/screens/${annTarget}`), { announcement: ann })
    target.catch((e) => console.warn('[tvcontrol] announce', e))
  }
  const clearAnnouncement = () => {
    if (annTarget === 'all') {
      // Clear the global one AND any per-screen overrides so nothing lingers.
      const updates = { announcement: null }
      rows.forEach(([id]) => { updates[`screens/${id}/announcement`] = null })
      update(tref('tvControl'), updates).catch((e) => console.warn('[tvcontrol] clear', e))
    } else {
      update(tref(`tvControl/screens/${annTarget}`), { announcement: null }).catch((e) => console.warn('[tvcontrol] clear', e))
    }
  }

  // Premium style tokens (TEST only) — cream cards, gold hairline, green serif
  // titles, dark-green/gold buttons. Falsy branch keeps the original dark theme.
  const P = PBCC_PREMIUM
  const annPanel = P
    ? { backgroundColor: 'var(--ge-cream-premium)', border: '1.5px solid var(--ge-gold)', color: 'var(--ge-primary)' }
    : { backgroundColor: 'rgba(var(--ge-primary-rgb),0.08)', border: '1px solid rgba(var(--ge-primary-rgb),0.30)' }
  const tvPanel = P
    ? { backgroundColor: 'var(--ge-cream-premium)', border: '1.5px solid var(--ge-gold)', color: 'var(--ge-primary)' }
    : { backgroundColor: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.10)' }
  const emptyPanel = P
    ? { backgroundColor: 'var(--ge-cream-premium)', border: '1.5px solid var(--ge-gold)', color: 'var(--ge-primary)' }
    : { backgroundColor: 'rgba(255,255,255,0.05)' }
  const heading = P ? { color: 'var(--ge-primary)', fontFamily: 'Cinzel, serif' } : { color: CLUB.accentColor }
  const inputBox = P
    ? { backgroundColor: '#FFFFFF', color: 'var(--ge-primary)', border: '1px solid rgba(var(--ge-primary-rgb),0.30)' }
    : { backgroundColor: 'rgba(255,255,255,0.10)', color: '#FFFFFF', border: '1px solid rgba(255,255,255,0.15)' }
  const goldBtn = P
    ? { backgroundColor: 'var(--ge-primary)', color: 'var(--ge-cream-premium)', border: '1px solid rgba(var(--ge-gold-rgb),0.85)' }
    : { backgroundColor: 'rgba(255,255,255,0.08)', color: '#FFFFFF', border: '1px solid rgba(255,255,255,0.15)' }
  const postBtnStyle = P
    ? { backgroundColor: 'var(--ge-primary)', color: 'var(--ge-cream-premium)', border: '1px solid rgba(var(--ge-gold-rgb),0.85)', opacity: annText.trim() ? 1 : 0.5 }
    : { backgroundColor: CLUB.accentColor, color: 'var(--ge-primary)', opacity: annText.trim() ? 1 : 0.5 }
  const sceneStyle = (on) => P
    ? (on
        ? { backgroundColor: 'var(--ge-primary)', color: 'var(--ge-cream-premium)', border: '1px solid var(--ge-gold)' }
        : { backgroundColor: '#FFFFFF', color: 'var(--ge-primary)', border: '1px solid rgba(var(--ge-primary-rgb),0.30)' })
    : { backgroundColor: on ? CLUB.accentColor : 'rgba(255,255,255,0.08)', color: on ? 'var(--ge-primary)' : '#FFFFFF', border: on ? 'none' : '1px solid rgba(255,255,255,0.15)' }
  const speedStyle = (on) => P
    ? (on
        ? { backgroundColor: 'var(--ge-gold)', color: 'var(--ge-primary)', border: '1px solid var(--ge-gold)' }
        : { backgroundColor: '#FFFFFF', color: 'var(--ge-primary)', border: '1px solid rgba(var(--ge-primary-rgb),0.30)' })
    : { backgroundColor: on ? '#FFFFFF' : 'rgba(255,255,255,0.08)', color: on ? 'var(--ge-primary)' : '#FFFFFF', border: on ? 'none' : '1px solid rgba(255,255,255,0.15)' }
  const muted = (op) => ({ color: P ? `rgba(var(--ge-primary-rgb),${op})` : `rgba(255,255,255,${op})` })

  const inner = (
    <>
      {/* Announcements — post a scrolling message over the live scene(s). */}
      <div className="rounded-2xl p-4 mb-6" style={annPanel}>
        <h2 className="text-lg font-extrabold mb-2 flex items-center gap-2" style={heading}>
          <img src={CLUB.logoLight} alt="" style={{ height: 56, width: 'auto' }} /> Announcements
        </h2>
        <textarea
          value={annText}
          onChange={(e) => setAnnText(e.target.value)}
          rows={2}
          placeholder="Type a message to scroll over the TV(s) — e.g. “Lunch served in the grill at noon.”"
          className="w-full rounded-lg p-2.5 text-sm"
          style={P
            // TEST: see-through field (the cream panel shows through) with dark
            // text so the message reads clearly. Club keeps its original input.
            ? { backgroundColor: 'transparent', color: 'var(--ge-primary)', border: '1px solid rgba(var(--ge-primary-rgb),0.30)', resize: 'vertical' }
            : { ...inputBox, resize: 'vertical' }}
        />
        <div className="flex items-center gap-2 mt-2 flex-wrap">
          <label className="flex items-center gap-2 text-xs" style={muted(0.7)}>
            <span className="uppercase tracking-widest">Target</span>
            <select
              value={annTarget}
              onChange={(e) => setAnnTarget(e.target.value)}
              className="rounded-lg px-2 py-1.5 text-sm font-bold"
              style={inputBox}>
              <option value="all" style={{ color: '#000' }}>All Screens</option>
              {rows.map(([id, s]) => (
                <option key={id} value={id} style={{ color: '#000' }}>TV {id}{s?.name ? ` — ${s.name}` : ''}</option>
              ))}
            </select>
          </label>
          <button
            onClick={postAnnouncement}
            disabled={!annText.trim()}
            className="px-4 py-2 rounded-lg font-extrabold text-sm transition active:scale-[0.98]"
            style={postBtnStyle}>
            📢 Post
          </button>
          <button
            onClick={clearAnnouncement}
            className="px-4 py-2 rounded-lg font-bold text-sm transition active:scale-[0.98]"
            style={goldBtn}>
            ✕ Clear
          </button>
          {P ? (
            <button
              type="button"
              onClick={() => setShowEmoji((v) => !v)}
              className="px-3 py-2 rounded-lg font-bold text-sm transition active:scale-[0.98]"
              style={goldBtn}
              aria-label="Add emoji">
              😊 Emoji
            </button>
          ) : null}
        </div>
        {P && showEmoji ? (
          <div className="mt-2 p-2 rounded-lg flex flex-wrap gap-1"
               style={{ backgroundColor: '#FFFFFF', border: '1px solid rgba(var(--ge-primary-rgb),0.30)' }}>
            {ANN_EMOJIS.map((e) => (
              <button
                key={e}
                type="button"
                onClick={() => { setAnnText((t) => `${t}${e}`); setShowEmoji(false) }}
                className="text-2xl leading-none px-1.5 py-1 rounded transition hover:scale-110"
                style={{ background: 'transparent' }}>
                {e}
              </button>
            ))}
          </div>
        ) : null}
        <p className="text-[11px] mt-2" style={muted(0.55)}>
          Scrolls up over whatever each screen is showing — the leaderboard/content stays visible behind it.
          {annTarget === 'all' ? ' Posting to every connected screen.' : ` Posting to TV ${annTarget} only.`}
        </p>
      </div>

      {rows.length === 0 ? (
        <div className="rounded-2xl p-8 text-center" style={emptyPanel}>
          <p className="text-xl font-bold mb-2">No screens connected yet</p>
          <p style={muted(0.6)}>
            On each TV, open <span className="font-mono font-bold">/tv?screen=1</span>,
            <span className="font-mono font-bold"> /tv?screen=2</span>, … (any number) and they'll
            appear here automatically.
          </p>
        </div>
      ) : (
        <div className="space-y-4">
          {rows.map(([id, s]) => {
            const live = now - Number(s?.lastSeen || 0) < LIVE_WINDOW_MS
            const mode = normalizeTvMode(s?.mode)
            const speed = SPEEDS.includes(Number(s?.rotateSeconds)) ? Number(s.rotateSeconds) : 30
            const pinnedId = s?.tournamentId || ''
            const pinned = pinnedId ? tournaments?.[pinnedId] : null
            const pinnedLabel = pinnedId
              ? (pinned ? `${tName(pinned)}${tBadge(pinned)}` : 'pinned (not found)')
              : 'Auto — current event'
            return (
              <div key={id} className="rounded-2xl p-4" style={tvPanel}>
                <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="text-2xl font-extrabold whitespace-nowrap">TV {id}</span>
                    <span className="text-2xl font-extrabold" style={muted(0.35)}>—</span>
                    <ScreenNameInput value={s?.name || ''} onSave={(v) => setName(id, v)} />
                    <span
                      className="text-xs font-bold uppercase tracking-wider px-2 py-1 rounded-full flex items-center gap-1.5"
                      style={{ backgroundColor: live ? 'rgba(22,163,74,0.20)' : (P ? 'rgba(var(--ge-primary-rgb),0.10)' : 'rgba(255,255,255,0.08)'), color: live ? (P ? '#15803D' : '#34D399') : (P ? 'rgba(var(--ge-primary-rgb),0.55)' : 'rgba(255,255,255,0.5)') }}>
                      <span className="w-2 h-2 rounded-full" style={{ backgroundColor: live ? '#22C55E' : '#6B7280' }} />
                      {live ? 'Live' : 'Offline'}
                    </span>
                    <button
                      onClick={() => removeScreen(id, s?.name)}
                      title={`Remove TV ${id} from the control panel`}
                      className="text-xs font-bold uppercase tracking-wider px-2.5 py-1 rounded-full transition active:scale-[0.98]"
                      style={{ backgroundColor: 'rgba(177,12,12,0.18)', color: P ? '#B10C0C' : '#FCA5A5', border: '1px solid rgba(177,12,12,0.40)' }}>
                      🗑 Remove
                    </button>
                  </div>

                  {/* Tournament pin — real or demo/practice */}
                  <label className="flex items-center gap-2 text-xs" style={muted(0.7)}>
                    <span className="uppercase tracking-widest">Tournament</span>
                    <select
                      value={pinnedId}
                      onChange={(e) => setTournament(id, e.target.value)}
                      className="rounded-lg px-2 py-1.5 text-sm font-bold"
                      style={inputBox}>
                      <option value="" style={{ color: '#000' }}>Auto — current event</option>
                      {tournOptions.map(([tid, v]) => (
                        <option key={tid} value={tid} style={{ color: '#000' }}>{tName(v)}{tBadge(v)}</option>
                      ))}
                    </select>
                  </label>
                </div>

                <div className="text-[11px] mb-2" style={muted(0.55)}>Showing: <span className="font-bold" style={{ color: P ? 'var(--ge-primary)' : 'rgba(255,255,255,0.7)' }}>{pinnedLabel}</span></div>

                {/* Scene buttons — all six real channels + Rotate */}
                <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-7 gap-2">
                  {TV_SCENES.map((m) => {
                    const on = mode === m.key
                    return (
                      <button
                        key={m.key}
                        onClick={() => setMode(id, m.key)}
                        className="py-3 rounded-xl font-extrabold text-xs transition active:scale-[0.98]"
                        style={sceneStyle(on)}>
                        <span className="block text-2xl leading-none mb-1">{m.icon}</span>
                        {m.label}
                      </button>
                    )
                  })}
                </div>

                {/* Rotate speed — only used in Rotate mode */}
                <div className="mt-3 flex items-center gap-2 flex-wrap" style={{ opacity: mode === 'rotate' ? 1 : 0.45 }}>
                  <span className="text-xs uppercase tracking-widest mr-1" style={muted(0.6)}>Rotate speed</span>
                  {SPEEDS.map((sp) => {
                    const on = speed === sp
                    return (
                      <button
                        key={sp}
                        onClick={() => setSpeed(id, sp)}
                        className="px-4 py-2 rounded-lg font-bold text-sm transition active:scale-[0.98]"
                        style={speedStyle(on)}>
                        {sp}s
                      </button>
                    )
                  })}
                </div>
              </div>
            )
          })}
        </div>
      )}
    </>
  )

  // Full tournament tab menu for the shared sidebar — identical to the other
  // admin pages. "📺 TV Screen Control" is THIS page (active); the rest deep-link
  // to the current tournament's detail with that tab.
  // Prefer the tournament the user came from (?t=…, carried by the tournament
  // detail's "TV Screen Control" tab) so the sidebar tabs return to THAT
  // tournament — never the auto-resolved current event. Falls back to the first
  // tournament only when TV Control is opened standalone (no ?t=).
  const currentTid = searchParams.get('t') || (tournOptions[0] && tournOptions[0][0]) || Object.keys(tournaments || {})[0] || null
  const TV_TABS = ['Overview', '📺 TV Monitor', '📺 TV Screen Control', 'Flights & Teams', 'Tee Times', 'QR Codes', 'Live Scores', 'Live Leaderboard', 'Bracket', 'Contests', 'Photos', 'Calcutta', 'Results']
  const TV_NAV_ITEMS = TV_TABS.map((t) => ({
    key: t,
    label: t,
    active: t === '📺 TV Screen Control',
    onClick: () => {
      if (t === '📺 TV Screen Control') return
      navigate(currentTid ? `/admin/t/${currentTid}?tab=${encodeURIComponent(t)}` : '/admin/pbcc-4ball')
    },
  }))

  return P ? (
    // Premium: the SHARED AdminShell — same full-height sidebar (pine logo,
    // global scale, #0E281D) as every other admin page, with the full tournament
    // tab menu. Content on the right.
    <AdminShell nav={{ items: TV_NAV_ITEMS }}>
      <div className="px-6 pt-6 pb-4">
        <h1 className="text-3xl" style={{ color: 'var(--ge-cream-premium)', fontFamily: 'Cinzel, serif', fontWeight: 700 }}>📺 TV Control</h1>
      </div>
      <div className="pbcc-main-panel-4ball pbcc-tvcontrol-body flex-1 min-h-0">
        <main className="p-4 sm:p-8">{inner}</main>
      </div>
    </AdminShell>
  ) : (
    <div className="min-h-screen" style={{ backgroundColor: 'var(--ge-primary)', color: '#FFFFFF' }}>
      <div className="max-w-5xl mx-auto px-4 py-6">
        <button
          onClick={() => navigate(-1)}
          className="inline-flex items-center gap-1 mb-4 text-xs font-bold uppercase tracking-widest transition"
          style={{ color: 'rgba(255,255,255,0.85)' }}
          onMouseEnter={(e) => { e.currentTarget.style.color = '#FFFFFF' }}
          onMouseLeave={(e) => { e.currentTarget.style.color = 'rgba(255,255,255,0.85)' }}>
          ← Back
        </button>
        <div className="flex items-center justify-between flex-wrap gap-2 mb-6">
          <h1 className="text-3xl font-extrabold" style={{ color: CLUB.accentColor }}>📺 TV Control</h1>
          <p className="text-sm text-white/60">{rows.length} screen{rows.length === 1 ? '' : 's'} connected</p>
        </div>
        {inner}
      </div>
    </div>
  )
}
